import { UpdateCustomerError } from 'Customer/hooks/useCustomerRoot'
import { match } from 'ts-pattern'
import { Text, Banner } from '@sequencehq/core-components'
import { Primary13SemiBold } from '@sequencehq/core-components'
import { CreateCustomerError } from 'modules/Customers/hooks/useCreateCustomer'

type CreateCustomerFormErrorBannerProps = {
  operation: 'CREATE'
  error?: CreateCustomerError
}

type EditCustomerFormErrorBannerProps = {
  operation: 'UPDATE'
  error?: UpdateCustomerError
}

type Props =
  | CreateCustomerFormErrorBannerProps
  | EditCustomerFormErrorBannerProps

export const CustomerFormErrorBanner = ({ error, operation }: Props) => {
  if (!error) {
    return null
  }

  return match(error.type)
    .with('INVALID_AVALARA_ADDRESS', () => (
      <Banner
        data-testid="customerForm.avalaraAddressErrorBanner"
        variant="error"
        alignIcon="center"
      >
        <Text {...Primary13SemiBold} color="inherit" marginBottom="4px">
          Address lookup failed:
        </Text>
        Please check address details for tax calculation with Avalara.
      </Banner>
    ))
    .with('CUSTOMER_ALIAS_ALREADY_IN_USE', () => {
      const identifiers = error.identifiers.map(i => `"${i}"`)
      const joinedIdentifiers =
        identifiers.length > 1
          ? `${identifiers.slice(0, -1).join(', ')} and ${identifiers[identifiers.length - 1]}`
          : identifiers[0] || ''

      return (
        <Banner
          data-testid="customerForm.customerAliasErrorBanner"
          variant="error"
          alignIcon="center"
        >
          <Text {...Primary13SemiBold} color="inherit" marginBottom="4px">
            Failed to {operation === 'CREATE' ? 'create' : 'update'} customer:
          </Text>
          {identifiers.length === 1
            ? `The alias ${joinedIdentifiers} is already in use.`
            : `The aliases ${joinedIdentifiers} are already in use.`}
        </Banner>
      )
    })
    .with('CONTACT_EMAIL_ALREADY_EXISTS', () => (
      <Banner
        data-testid="customerForm.contactEmailErrorBanner"
        variant="error"
        alignIcon="center"
      >
        <Text {...Primary13SemiBold} color="inherit" marginBottom="4px">
          Failed to {operation === 'CREATE' ? 'create' : 'update'} customer:
        </Text>
        A contact with this email already exists.
      </Banner>
    ))
    .with('OTHER', () => (
      <Banner
        data-testid="customerForm.otherErrorBanner"
        variant="error"
        alignIcon="center"
      >
        <Text {...Primary13SemiBold} color="inherit" marginBottom="4px">
          Failed to {operation === 'CREATE' ? 'create' : 'update'} customer:
        </Text>
        {error.messages.length > 0
          ? error.messages.join('. ')
          : 'Please try again.'}
      </Banner>
    ))
    .exhaustive()
}
